/**
 * Custom Employee Field Builder
 * Lets company owners define brand-new fields that appear in the employee
 * form and detail page. Stored in company_settings.custom_employee_fields.
 * Accessible from: EmployeeForm (inline button) + Settings → Employee Fields.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { X, Plus, Trash2, GripVertical, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const FIELD_TYPES = [
  { value: 'text',     label: 'Text Input',     description: 'Single line of text' },
  { value: 'number',   label: 'Number',         description: 'Numeric value' },
  { value: 'checkbox', label: 'Checkbox',       description: 'Yes / No toggle' },
  { value: 'dropdown', label: 'Dropdown',       description: 'Select from options you define' },
  { value: 'textarea', label: 'Long Text',      description: 'Multi-line text area' },
  { value: 'date',     label: 'Date',           description: 'Date picker' },
  { value: 'photo',    label: 'Photo / File',   description: 'Image or document upload' },
];

const SWATCH_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#6b7280',
];

const inputCls = 'w-full h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';

function slugify(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function FieldEditor({ field, onChange, onDelete }) {
  const [open, setOpen] = useState(!field.label);
  const [newOption, setNewOption] = useState('');

  const update = (patch) => onChange({ ...field, ...patch });
  const addOption = () => {
    const t = newOption.trim();
    if (!t || (field.options || []).some(o => o.label === t)) return;
    update({ options: [...(field.options || []), { label: t, color: SWATCH_COLORS[(field.options || []).length % SWATCH_COLORS.length] }] });
    setNewOption('');
  };
  const removeOption = (i) => update({ options: field.options.filter((_, idx) => idx !== i) });
  const updateOptionColor = (i, color) => update({ options: field.options.map((o, idx) => idx === i ? { ...o, color } : o) });

  return (
    <div className="rounded-[12px] border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <GripVertical size={14} className="text-[var(--text-tertiary)] shrink-0"/>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
            {field.label || <span className="text-[var(--text-tertiary)] font-normal italic">Unnamed field</span>}
          </p>
          <p className="text-[11px] text-[var(--text-tertiary)]">{FIELD_TYPES.find(t => t.value === field.type)?.label}</p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setOpen(o => !o)}
            className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">
            {open ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
          </button>
          <button type="button" onClick={onDelete}
            className="p-1.5 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)]">
            <Trash2 size={13}/>
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[var(--border)]">
          <div className="grid grid-cols-2 gap-3 pt-3">
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Field Label *</label>
              <input value={field.label} onChange={e => update({ label: e.target.value, key: slugify(e.target.value) })}
                placeholder="e.g. Employee T-Shirt Size" className={inputCls}/>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Type</label>
              <select value={field.type} onChange={e => update({ type: e.target.value, options: [] })} className={inputCls}>
                {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Placeholder</label>
              <input value={field.placeholder || ''} onChange={e => update({ placeholder: e.target.value })}
                placeholder="Optional hint text" className={inputCls}/>
            </div>
            <div className="flex items-end pb-0.5 gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!field.required}
                  onChange={e => update({ required: e.target.checked })}
                  className="w-4 h-4 rounded accent-[var(--accent)]"/>
                <span className="text-[13px] text-[var(--text-primary)]">Required field</span>
              </label>
            </div>
          </div>

          {field.type === 'dropdown' && (
            <div className="space-y-2">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Options</label>
              <div className="space-y-1.5">
                {(field.options || []).map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex gap-1 shrink-0">
                      {SWATCH_COLORS.map(c => (
                        <button key={c} type="button" onClick={() => updateOptionColor(i, c)}
                          style={{ backgroundColor: c }}
                          className={cn('w-4 h-4 rounded-full transition-all', opt.color === c ? 'ring-2 ring-offset-1 ring-[var(--accent)]' : 'opacity-60 hover:opacity-100')}/>
                      ))}
                    </div>
                    <span className="flex-1 text-[13px] text-[var(--text-primary)]" style={{ color: opt.color }}>{opt.label}</span>
                    <button type="button" onClick={() => removeOption(i)}
                      className="text-[var(--text-tertiary)] hover:text-[var(--danger)] p-1"><Trash2 size={11}/></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input value={newOption} onChange={e => setNewOption(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                    placeholder="Add option…" className={inputCls}/>
                  <button type="button" onClick={addOption}
                    className="shrink-0 h-9 px-3 rounded-[8px] bg-[var(--accent)] text-white text-[12px] font-semibold hover:opacity-90 transition-opacity">
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function newField() {
  return { key: '', label: '', type: 'text', required: false, placeholder: '', options: [] };
}

export function CustomFieldBuilderModal({ onClose }) {
  const { data: settingsResp } = useCompanySettings();
  const update = useUpdateCompanySettings();

  const [fields, setFields] = useState(() =>
    (settingsResp?.data?.settings?.custom_employee_fields || []).map(f => ({ ...f }))
  );

  const addField = () => setFields(fs => [...fs, newField()]);
  const updateField = (i, f) => setFields(fs => fs.map((old, idx) => idx === i ? f : old));
  const deleteField = (i) => setFields(fs => fs.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    const invalid = fields.filter(f => !f.label.trim());
    if (invalid.length) { toast.error('All fields must have a label'); return; }

    // Ensure unique keys
    const withKeys = fields.map(f => ({ ...f, key: f.key || slugify(f.label) || `custom_${Date.now()}` }));
    const keys = withKeys.map(f => f.key);
    if (new Set(keys).size !== keys.length) { toast.error('Field labels must be unique'); return; }

    try {
      const current = settingsResp?.data?.settings || {};
      await update.mutateAsync({ settings: { ...current, custom_employee_fields: withKeys } });
      toast.success(`${withKeys.length} custom field${withKeys.length !== 1 ? 's' : ''} saved`);
      onClose();
    } catch {
      toast.error('Failed to save custom fields');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-lg border border-[var(--border)] flex flex-col max-h-[90vh] fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-[var(--border)] shrink-0">
          <div className="w-8 h-8 rounded-[8px] bg-[var(--accent)]/10 flex items-center justify-center">
            <Zap size={15} className="text-[var(--accent)]"/>
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-[var(--text-primary)]">Custom Employee Fields</h2>
            <p className="text-[11px] text-[var(--text-tertiary)]">Your company-specific fields — visible only to you</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">
            <X size={15}/>
          </button>
        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {fields.length === 0 && (
            <div className="text-center py-8 text-[14px] text-[var(--text-tertiary)]">
              No custom fields yet. Click "Add Field" to create your first one.
            </div>
          )}
          {fields.map((f, i) => (
            <FieldEditor key={i} field={f}
              onChange={updated => updateField(i, updated)}
              onDelete={() => deleteField(i)}/>
          ))}
          <button type="button" onClick={addField}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-[var(--accent)] hover:underline">
            <Plus size={13}/> Add Field
          </button>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-[var(--border)] shrink-0">
          <Button onClick={handleSave} loading={update.isPending} className="flex-1">Save Fields</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
